// The captain portrait service (R1180, ui-map-interface.md §12 and §16.3).
//
// A panel asks for the markup of one portrait. When the picture is ready,
// the markup is an `<img>` of the cached blob URL. When it is not, the
// markup is the plain placeholder circle with a `data-portrait` key, and a
// job starts. When the job ends, every element with that key takes the
// image in place, so the panel neither re-renders nor flashes; the panel's
// next render writes the `<img>` itself, with the same URL.
//
// The cost (§10): the pixels are made in a Web Worker, one job at a time,
// and the main thread only draws the gear on a canvas of the picture's own
// size and makes the blob URL. Where no worker starts, one job runs per
// idle slice instead. The cache holds one URL per seed, CSS size, device
// pixel ratio and faction, and a job for one key never starts twice.

import { factionOf } from '../../world';
import { PORTRAIT } from '../../gen/presentation';
import { rgba } from '../../render/color';
import { deviceRatio } from '../../render/dpr';
import { isFemale, portraitSeed, type Phase } from './seed';
import type { ThermalJob, ThermalPixels } from './thermal';

/** Who a portrait shows. `faction` is 0 where no banner is known.
 *  `seed` and `female` are the face the Captain Designer froze (R1240,
 *  monetization.md §3.1); where they are absent, the face comes from the
 *  name and the talents. `storedLooks` of `seed.ts` reads the wire pair. */
export interface PortraitSpec {
  name: string;
  talents: readonly number[];
  phase: Phase;
  faction: number;
  seed?: number;
  female?: boolean;
}

/** One picture to compose: the job, and what the gear draws. */
export interface PortraitPicture {
  job: ThermalJob;
  cssPx: number;
  /** The faction colour of the dot, or null for no dot. */
  dot: number | null;
  /** True when the brackets, the reading and the dot draw. */
  gear: boolean;
}

/** What the service needs from the platform. The tests pass their own. */
export interface PortraitDeps {
  /** Makes the pixels of one job. */
  pixels(job: ThermalJob): Promise<ThermalPixels>;
  /** Turns the pixels into an image URL. */
  compose(pic: PortraitPicture, px: ThermalPixels): Promise<string>;
  /** The device pixel ratio now. */
  dpr(): number;
  /** Puts the ready image into every placeholder of the key. */
  fill(key: string, url: string): void;
}

/** The markup service. `createPortraits` makes one; the chrome uses `portraitHtml`. */
export interface Portraits {
  /** The markup of one portrait of `cssPx`, in a box of class `cls`. */
  html(spec: PortraitSpec, cssPx: number, cls: string): string;
  /** The cached URL of a key, if the picture is ready. */
  url(key: string): string | undefined;
  /** Resolves when the picture of a key is ready (tests and callers that wait). */
  ready(key: string): Promise<string> | undefined;
}

/** The key of one picture. The sex is part of it, because the gender
 *  toggle of the Captain Designer draws two faces from one seed. */
function portraitKey(
  seed: number, female: boolean, cssPx: number, px: number, faction: number,
): string {
  return `${seed}-${female ? 'f' : 'm'}-${cssPx}-${px}-${faction}`;
}

/** Makes a portrait service over the given platform. */
export function createPortraits(deps: PortraitDeps): Portraits {
  const urls = new Map<string, string>();
  const jobs = new Map<string, Promise<string>>();

  function plan(spec: PortraitSpec, cssPx: number): { key: string; pic: PortraitPicture } {
    const seed = spec.seed !== undefined && spec.seed > 0
      ? spec.seed >>> 0
      : portraitSeed(spec.name, spec.talents);
    const female = spec.female ?? isFemale(spec.name);
    const ratio = Math.min(PORTRAIT.maxDpr, Math.max(1, deps.dpr() || 1));
    const px = Math.max(8, Math.round(cssPx * ratio));
    const gear = cssPx >= PORTRAIT.gearMinPx;
    // The dot stands on the thermal photo, and the photo keeps its own dark
    // ground in both identities, so the dot takes the dark faction ink
    // (R3320 ruling 9, R3321).
    const colour = gear ? factionOf(spec.faction)?.banner : undefined;
    const faction = colour === undefined ? 0 : spec.faction;
    return {
      key: portraitKey(seed, female, cssPx, px, faction),
      pic: {
        job: { seed, female, phase: spec.phase, px },
        cssPx, gear, dot: colour ?? null,
      },
    };
  }

  function start(key: string, pic: PortraitPicture): Promise<string> {
    let p = jobs.get(key);
    if (!p) {
      p = deps.pixels(pic.job)
        .then((px) => deps.compose(pic, px))
        .then((url) => {
          urls.set(key, url);
          deps.fill(key, url);
          return url;
        });
      // A failed job leaves the placeholder, and a later render may try again.
      p.catch(() => { jobs.delete(key); });
      jobs.set(key, p);
    }
    return p;
  }

  return {
    html(spec, cssPx, cls) {
      const { key, pic } = plan(spec, cssPx);
      const url = urls.get(key);
      if (url) return `<div class="${cls}">${imgTag(url)}</div>`;
      void start(key, pic).catch(() => undefined);
      return `<div class="${cls}" data-portrait="${key}"></div>`;
    },
    url: (key) => urls.get(key),
    ready: (key) => jobs.get(key),
  };
}

/** The image of a ready portrait. It is decoration: the name stands beside it. */
function imgTag(url: string): string {
  return `<img src="${url}" alt="" draggable="false">`;
}

// ---------- the browser platform ----------

type Pending = { resolve: (p: ThermalPixels) => void; reject: (e: unknown) => void };

let worker: Worker | null | undefined;
let nextId = 1;
const waiting = new Map<number, Pending>();

/** The render worker, or null where none starts. */
function renderWorker(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    worker = typeof Worker === 'undefined'
      ? null
      : new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  } catch {
    worker = null;
  }
  if (worker) {
    worker.onmessage = (e: MessageEvent<{ id: number; N: number; data: Uint8ClampedArray }>) => {
      const w = waiting.get(e.data.id);
      waiting.delete(e.data.id);
      w?.resolve({ N: e.data.N, data: e.data.data });
    };
    worker.onerror = () => {
      // The worker failed to load: every job waiting and every later job
      // runs on the main thread instead.
      worker = null;
      const jobs = [...waiting.values()];
      waiting.clear();
      for (const w of jobs) w.reject(new Error('portrait worker failed'));
    };
  }
  return worker;
}

/** The main-thread fallback: one job per idle slice. */
let idleChain: Promise<unknown> = Promise.resolve();
function idleSlice(): Promise<void> {
  return new Promise((resolve) => {
    const ric = (globalThis as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
    if (ric) ric(() => resolve());
    else setTimeout(resolve, 0);
  });
}
function pixelsHere(job: ThermalJob): Promise<ThermalPixels> {
  const run = idleChain.then(idleSlice).then(async () => {
    const { thermalPixels } = await import('./thermal');
    return thermalPixels(job);
  });
  idleChain = run.catch(() => undefined);
  return run;
}

function browserPixels(job: ThermalJob): Promise<ThermalPixels> {
  const w = renderWorker();
  if (!w) return pixelsHere(job);
  const id = nextId++;
  return new Promise<ThermalPixels>((resolve, reject) => {
    waiting.set(id, { resolve, reject });
    w.postMessage({ id, job });
  }).catch(() => pixelsHere(job));
}

/** Draws the pixels and the card's gear, and makes a blob URL. */
async function browserCompose(pic: PortraitPicture, px: ThermalPixels): Promise<string> {
  const size = pic.job.px;
  const src = document.createElement('canvas');
  src.width = src.height = px.N;
  src.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(px.data), px.N, px.N), 0, 0);
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const k = cv.getContext('2d')!;
  k.imageSmoothingEnabled = true;
  k.imageSmoothingQuality = 'high';
  k.drawImage(src, 0, 0, size, size);
  if (pic.gear) {
    // The card draws in a circle of radius r around the centre, in CSS px.
    const r = pic.cssPx / 2;
    k.setTransform(size / pic.cssPx, 0, 0, size / pic.cssPx, 0, 0);
    k.translate(r, r);
    const decline = pic.job.phase === 'decline' ? PORTRAIT.tempDecline : 0;
    const t = (PORTRAIT.tempBase + (pic.job.seed % PORTRAIT.tempSteps) / 10 - decline).toFixed(1);
    const fs = Math.max(5, r * 0.15);
    k.font = `500 ${fs}px ${PORTRAIT.font.join(', ')}`;
    k.fillStyle = rgba(PORTRAIT.gear, PORTRAIT.textAlpha);
    k.textAlign = 'right';
    k.textBaseline = 'middle';
    k.fillText(`${t}°`, r * 0.6, r * 0.72);
    k.lineWidth = Math.max(0.6, r * 0.02);
    k.lineCap = 'round';
    k.lineJoin = 'round';
    k.strokeStyle = rgba(PORTRAIT.gear, PORTRAIT.gearAlpha);
    k.beginPath();
    k.moveTo(-r * 0.46, -r * 0.62); k.lineTo(-r * 0.62, -r * 0.62); k.lineTo(-r * 0.62, -r * 0.46);
    k.moveTo(r * 0.46, r * 0.42); k.lineTo(r * 0.62, r * 0.42); k.lineTo(r * 0.62, r * 0.26);
    k.stroke();
    if (pic.dot !== null) {
      k.beginPath();
      k.arc(-r * 0.5, r * 0.64, r * 0.045, 0, Math.PI * 2);
      k.fillStyle = rgba(pic.dot, 1);
      k.fill();
    }
  }
  const blob = await new Promise<Blob | null>((resolve) => cv.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('portrait: no image');
  const url = URL.createObjectURL(blob);
  // Decode once now, so the first <img> of the URL paints at once.
  const img = new Image();
  img.src = url;
  await img.decode().catch(() => undefined);
  return url;
}

function browserFill(key: string, url: string): void {
  if (typeof document === 'undefined') return;
  for (const el of document.querySelectorAll<HTMLElement>(`[data-portrait="${key}"]`)) {
    el.removeAttribute('data-portrait');
    el.innerHTML = imgTag(url);
  }
}

let shared: Portraits | null = null;

/**
 * The markup of one captain portrait of `cssPx` in a box of class `cls`.
 * One service serves every panel, so the hub and the game share the cache.
 */
export function portraitHtml(spec: PortraitSpec, cssPx: number, cls: string): string {
  shared ??= createPortraits({
    pixels: browserPixels,
    compose: browserCompose,
    dpr: () => deviceRatio(),
    fill: browserFill,
  });
  return shared.html(spec, cssPx, cls);
}
